/**
 * Enemy mechanic visuals (+encounters, docs/design/encounters.md §6). Pure data, no three.js.
 *
 * - `ENEMY_VFX`: hand-tuned specs for the reworked boss/trash abilities, merged into
 *   `ENEMY_OVERRIDES` in table.ts (one registration line).
 * - `ENEMY_AURA_VFX`: loops for the new enemy debuffs/buffs (for `resolveAuraVfx` callers).
 * - `HAZARD_LOOK` + `hazardLook()`: floor language for telegraphs and zones by `intent`
 *   (avoid / deadly / soak), poison pools, tethers and tracking beams.
 *
 * Colour rules: enemy danger is warm orange/red; enemy poison is a sickly yellow-green that never
 * matches the friendly heal teal or holy gold; all colours are linear RGB below the bloom
 * threshold and floor alphas stay ≤ 0.35 so the floor remains readable.
 *
 * Only *types* are imported from table.ts (no runtime cycle).
 */
import type { AbilityId, HazardIntent, Telegraph, Zone } from '@keystone/sim';
import { ENEMY, ENEMY_AURA } from '@keystone/content';
import type { AuraVfx, RGB, VfxSpec } from './table';

/** Enemy hazard palette (linear RGB). */
export const HAZARD = {
  avoid: [0.85, 0.38, 0.12],
  avoidRim: [0.95, 0.55, 0.2],
  deadly: [0.75, 0.12, 0.08],
  deadlyRim: [0.92, 0.22, 0.12],
  soak: [0.95, 0.7, 0.25],
  soakPip: [0.95, 0.93, 0.86],
  poison: [0.45, 0.6, 0.12],
  poisonRim: [0.3, 0.42, 0.08],
  tether: [0.8, 0.16, 0.1],
  beamCore: [0.9, 0.5, 0.15],
  beamEdge: [0.7, 0.22, 0.08],
  blood: [0.6, 0.06, 0.08],
  bloodCore: [0.85, 0.25, 0.2],
  bone: [0.85, 0.8, 0.68],
  boneCore: [0.98, 0.94, 0.85],
  bile: [0.62, 0.66, 0.1],
  bileCore: [0.86, 0.82, 0.3],
} as const satisfies Record<string, RGB>;

/** How a telegraph/zone is drawn on the floor. */
export interface HazardLook {
  /** Fill colour (none for hatched soaks). */
  fill: RGB | null;
  rim: RGB;
  /** Max floor alpha (≤ 0.35). */
  alpha: number;
  /** 'fill' grows toward resolution; 'hatch' = diagonal stripes (stand-in areas); 'pool' = matte zone. */
  pattern: 'fill' | 'hatch' | 'pool' | 'band';
  /** Rim pulse frequency (Hz); 0 = steady. */
  pulseHz: number;
  /** Draw soaker pips (●○) from Telegraph.soak. */
  pips?: RGB;
  /** FX budget tier (budget.ts): 5 = danger. */
  tier: number;
}

export const HAZARD_LOOK: Readonly<Record<HazardIntent | 'poison' | 'beam' | 'moving', HazardLook>> = {
  avoid: { fill: HAZARD.avoid, rim: HAZARD.avoidRim, alpha: 0.3, pattern: 'fill', pulseHz: 0, tier: 5 },
  deadly: { fill: HAZARD.deadly, rim: HAZARD.deadlyRim, alpha: 0.32, pattern: 'fill', pulseHz: 1, tier: 5 },
  soak: { fill: null, rim: HAZARD.soak, alpha: 0.35, pattern: 'hatch', pulseHz: 0, pips: HAZARD.soakPip, tier: 5 },
  poison: { fill: HAZARD.poison, rim: HAZARD.poisonRim, alpha: 0.3, pattern: 'pool', pulseHz: 0, tier: 4 },
  beam: { fill: HAZARD.beamCore, rim: HAZARD.beamEdge, alpha: 0.35, pattern: 'band', pulseHz: 0, tier: 5 },
  moving: { fill: HAZARD.avoid, rim: HAZARD.avoidRim, alpha: 0.3, pattern: 'fill', pulseHz: 0, tier: 5 },
};

/** Floor look for a live telegraph or zone (enemy-sourced). */
export function hazardLook(h: Pick<Telegraph, 'intent'> | Pick<Zone, 'intent' | 'motion' | 'tick'>): HazardLook {
  const motion = 'motion' in h ? h.motion : undefined;
  if (motion?.spec.type === 'aim') return HAZARD_LOOK.beam;
  if (h.intent === 'soak') return HAZARD_LOOK.soak;
  if (h.intent === 'deadly') return HAZARD_LOOK.deadly;
  if (motion && motion.spec.type !== 'grow') return HAZARD_LOOK.moving;
  // Persistent ticking ground = enemy poison pool unless flagged otherwise.
  if ('tick' in h && h.tick) return HAZARD_LOOK.poison;
  return HAZARD_LOOK.avoid;
}

/** Tether line (Death Rattle): 0.3 yd red line from player to source. */
export const TETHER_LOOK = { color: HAZARD.tether, width: 0.3, alpha: 0.6 } as const;
/** Ring drawn on a player holding an `AuraDef.hazard` debuff (spread / isolate). */
export const SPREAD_RING_LOOK = { color: HAZARD.avoid, alpha: 0.25, width: 0.25 } as const;
/** The unlit safe wedge drawn inside a `behindCaster` cone (Bloodroar). */
export const SAFE_WEDGE_LOOK = { dim: 0.45 } as const;

/** Hand-tuned looks for the reworked enemy abilities (merged into ENEMY_OVERRIDES). */
export const ENEMY_VFX: Partial<Record<AbilityId, Partial<VfxSpec>>> = {
  // trash
  [ENEMY.bloodFrenzy]: { release: 'flash', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY.ascensionRite]: { cast: 'shadow', release: 'flash', color: [0.7, 0.18, 0.45], accent: [0.95, 0.5, 0.75] },
  [ENEMY.piercingHiss]: { cast: 'venom', release: 'nova', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY.venomRain]: { cast: 'venom', release: 'none', impact: 'venom' },
  [ENEMY.petrifyingGlare]: { cast: 'shadow', release: 'flash', color: [0.85, 0.55, 0.4], accent: [1.0, 0.85, 0.6] },
  // Ravager
  [ENEMY.regurgitate]: { cast: 'windup', release: 'wave', color: HAZARD.bile, accent: HAZARD.bileCore, impact: 'venom' },
  [ENEMY.feedingFrenzy]: { cast: 'windup', release: 'none', color: HAZARD.blood, accent: HAZARD.bloodCore, impact: 'slash' },
  [ENEMY.huntingPounce]: { cast: 'windup', release: 'charge', impact: 'heavy', big: true },
  [ENEMY.rendingMaw]: { cast: 'windup', release: 'swing', swing: 'overhead', impact: 'heavy', big: true, color: HAZARD.blood, accent: HAZARD.bloodCore },
  // The Coil
  [ENEMY.synchronizedVenom]: { cast: 'venom', release: 'nova', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY.deathRattle]: { cast: 'shadow', release: 'nova', color: HAZARD.tether, accent: HAZARD.deadlyRim },
  [ENEMY.venomTorrent]: { cast: 'venom', release: 'beam', color: HAZARD.beamEdge, accent: HAZARD.beamCore },
  [ENEMY.coilingVortex]: { cast: 'venom', release: 'nova', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY.burrowingCharge]: { cast: 'windup', release: 'charge', impact: 'heavy' },
  [ENEMY.recoil]: { cast: 'windup', release: 'charge', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  // Chieftain Vashk
  [ENEMY.ritualOfTheFang]: { cast: 'shadow', release: 'beam', color: HAZARD.soak, accent: HAZARD.soakPip },
  [ENEMY.ritualOfTheFangP2]: { cast: 'shadow', release: 'beam', color: HAZARD.soak, accent: HAZARD.soakPip },
  [ENEMY.axegrinder]: { cast: 'windup', release: 'none', impact: 'slash', color: HAZARD.bone, accent: HAZARD.boneCore },
  [ENEMY.bloodroar]: { cast: 'windup', release: 'nova', impact: 'heavy', big: true, color: HAZARD.deadly, accent: HAZARD.deadlyRim },
  [ENEMY.bloodrage]: { release: 'flash', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY.bloodSacrifice]: { cast: 'shadow', release: 'beam', color: HAZARD.blood, accent: HAZARD.bloodCore },
};

/** Loops for the new enemy auras (callers of resolveAuraVfx may fall back to this). */
export const ENEMY_AURA_VFX: Readonly<Record<string, AuraVfx>> = {
  [ENEMY_AURA.bloodFrenzy]: { style: 'burning', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodFrenzyEmpowered]: { style: 'burning', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.hemorrhage]: { style: 'bleed', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.envenom]: { style: 'venom', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY_AURA.ascended]: { style: 'ritual', color: [0.7, 0.18, 0.45], accent: [0.95, 0.5, 0.75] },
  [ENEMY_AURA.coagulatingVenom]: { style: 'venom', color: HAZARD.blood, accent: HAZARD.poison },
  [ENEMY_AURA.petrifiedBrittle]: { style: 'petrified', color: [0.6, 0.58, 0.55], accent: [0.9, 0.85, 0.75] },
  [ENEMY_AURA.septicBite]: { style: 'venom', color: HAZARD.bile, accent: HAZARD.bileCore },
  [ENEMY_AURA.barbed]: { style: 'bleed', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bileBurn]: { style: 'venom', color: HAZARD.bile, accent: HAZARD.bileCore },
  [ENEMY_AURA.gorgedHide]: { style: 'ward', color: HAZARD.bile, accent: HAZARD.bileCore },
  [ENEMY_AURA.feedingFrenzy]: { style: 'burning', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.gorged]: { style: 'smash', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.syncVenom]: { style: 'venom', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY_AURA.syncVenomEmpowered]: { style: 'venom', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY_AURA.sluggishVenom]: { style: 'venom', color: HAZARD.poison, accent: HAZARD.tether },
  [ENEMY_AURA.burrowed]: { style: 'ward', color: [0.42, 0.36, 0.28], accent: [0.7, 0.62, 0.5] },
  [ENEMY_AURA.venomJetPoison]: { style: 'venom', color: HAZARD.poison, accent: [0.8, 0.85, 0.35] },
  [ENEMY_AURA.ritualVenom]: { style: 'ritual', color: HAZARD.soak, accent: HAZARD.deadlyRim },
  [ENEMY_AURA.fangEmpowerment]: { style: 'smash', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodOffering]: { style: 'ward', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodSacrifice]: { style: 'ritual', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodroarBleed]: { style: 'bleed', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodrage]: { style: 'burning', color: HAZARD.blood, accent: HAZARD.bloodCore },
  [ENEMY_AURA.bloodrageEmpowered]: { style: 'burning', color: HAZARD.blood, accent: HAZARD.bloodCore },
};
